import React, { useEffect, useState } from "react"
import { Link } from "react-router-dom"
import AsyncSelect from "react-select/async"
import moment from "moment-timezone"
import { get } from "helpers/api_helper"
import { daysYearLabel } from "./generationLabels"
import { instantLabel } from "./plannerLabels"
import { editorQuery } from "./proposalForm"
import { SOURCE_LABELS, changeText, sourcePill, valueSource } from "./proposalHistory"
import { RELOADING_CODES, UNCONFIRMED, proposalMessage } from "./proposalMessages"
import { SourcePill, portalMenu } from "./ProposalEventFields"
import PlannerDialog from "./PlannerDialog"

// „Ediția anterioară” in the editor (wayfinder ticket 09; D3's change read): the event a proposal continues, named, linked and with its source;
// changing it takes the new edition's technical characteristics or keeps the current values.

const changePath = (proposalId, eventId) => `/admin/tournament-proposals/${proposalId}/previous-edition-change`
  + (eventId ? "?" + new URLSearchParams({ eventId }) : "")
// The change read (D3): the previous edition as it is, and with `eventId` the event chosen instead and what Take would change. Read again
// with each revision of the proposal.
function useChangeRead(proposalId, revision, eventId, wanted = true) {
  const [state, setState] = useState({})
  const key = [proposalId, revision, eventId].join("|")
  useEffect(() => {
    if (!proposalId || !wanted) return undefined
    let live = true
    get(changePath(proposalId, eventId))
      .then(response => live && setState({ key, data: response?.data || null }))
      .catch(error => live && setState({ key, error: error?.response?.data?.message || "UNAVAILABLE" }))
    return () => { live = false }
  }, [proposalId, revision, eventId, wanted, key])
  return state.key === key ? state : {}
}

// "#38652 PLATINUM IASI, Fri 14 – Sun 16 Nov 2025".
const editionLabel = edition => `#${edition.eventNumber} ${edition.name}${edition.days ? `, ${daysYearLabel(edition.days)}` : ""}`
const shortLabel = edition => (edition ? `#${edition.eventNumber} ${edition.name}` : "No previous edition")
// Its source (PRD story 37): from generation, deduced, or chosen by a SuperAdmin (then who and when, from the history).
const PREVIOUS_SOURCES = { generation: SOURCE_LABELS.generation, deduced: "Deduced" }
const sourceOf = (previousEdition, context) => (PREVIOUS_SOURCES[previousEdition.source]
  ? { kind: "source", label: PREVIOUS_SOURCES[previousEdition.source] } : valueSource(context, "previousEdition") || { kind: "source", label: "Chosen" })

// „Ediția anterioară” deduced (ticket 09, D3's editor read) from the editor's values as they stand, once club, organizer, category, sport, play
// mode and dates are set; read 0.4 s after they stop changing. The last answer stays while no read is asked (the previous edition is set).
export const SUGGESTION_MS = 400
const SUGGESTION_PATH = "/admin/tournament-proposal-editor/previous-edition"
function useEditionSuggestion(path) {
  const [answer, setAnswer] = useState(null)
  useEffect(() => {
    if (!path) return undefined
    let live = true
    const timer = setTimeout(() => get(path).then(response => live && setAnswer(response?.data?.suggestion || null)).catch(() => live && setAnswer(null)),
      SUGGESTION_MS)
    return () => { live = false; clearTimeout(timer) }
  }, [path])
  return answer
}
// The deduction in the editor while the form has no previous edition (Tudor's Q3 A, 2026-10-21): a new proposal takes the edition deduced at
// once, unless "Don't use it" dropped it; a saved proposal is only offered it, since one saved without a previous edition may have been given
// "No previous edition" on purpose. Once set, the previous edition stays when the other fields change.
export function useDeducedEdition({ editing, values, setValues, errors, proposalId }) {
  const [dismissed, setDismissed] = useState(false)
  const deducing = !values.previousEdition && values.club?.kind === "club" && Boolean(values.organizer) && Boolean(values.playMode) && !errors.category
    && !errors.startDay && !errors.endDay
  const suggestion = useEditionSuggestion(deducing ? `${SUGGESTION_PATH}?${editorQuery(values, proposalId ? { proposalId } : {})}` : null)
  const filling = !editing && !dismissed && deducing ? suggestion : null
  useEffect(() => {
    if (filling) setValues(current => (current.previousEdition ? current : { ...current, previousEdition: { eventId: filling.eventId, source: "deduced" } }))
  }, [filling, setValues])
  const take = found => setValues(current => ({ ...current, previousEdition: { eventId: found.eventId, source: "deduced" } }))
  const dismiss = () => {
    setDismissed(true)
    setValues(current => ({ ...current, previousEdition: null }))
  }
  return { suggestion, take, dismiss }
}

// The editor's line: the previous edition (saved, or deduced and not saved yet), or none with the edition deduced to use, and "Change…" for an
// open proposal saved without unsaved changes.
export function PreviousEditionLine({ proposal, edition, deduced, context, canChange, onChange }) {
  const saved = proposal?.previousEdition || null
  const read = useChangeRead(proposal?.id, proposal?.revision, null, Boolean(saved))
  const savedEdition = read.data?.current
  const { suggestion } = deduced
  let shown
  if (edition && edition.eventId !== saved?.eventId) {
    shown = <>
      <span>{suggestion?.eventId === edition.eventId ? editionLabel(suggestion) : `event ${edition.eventId}`}</span>
      <SourcePill source={{ kind: "source", label: PREVIOUS_SOURCES.deduced }} />
      <button type="button" className="btn btn-link btn-sm p-0" onClick={deduced.dismiss}>Don't use it</button>
    </>
  } else if (saved && edition) {
    shown = <>
      {savedEdition?.eventNumber ? <Link to={`/events/${savedEdition.eventId}`}>{editionLabel(savedEdition)}</Link>
        : <span>{read.error ? `event ${saved.eventId}` : "loading…"}</span>}
      <SourcePill source={sourceOf(saved, context)} />
    </>
  } else {
    shown = <>
      <span>None</span>
      {!edition && suggestion && <span className="ep-previous-suggestion">
        <span>{`Suggested previous edition: ${editionLabel(suggestion)}`}</span>
        <button type="button" className="btn btn-link btn-sm p-0" onClick={() => deduced.take(suggestion)}>Use it</button>
      </span>}
    </>
  }
  return <div role="group" aria-label="Previous edition" className="ep-previous-edition">
    <span className="ep-meta">Previous edition:</span>
    {shown}
    {canChange && <button type="button" className="btn btn-link btn-sm p-0" onClick={onChange}>Change…</button>}
  </div>
}

// Changing the previous edition of a Proposed or Confirmed proposal asks whether to tell its organizer (ticket 09).
const ASKS_TO_TELL = ["proposed", "confirmed"]
const READ_REFUSALS = {
  INVALID_PROPOSAL_QUERY: "This event can't be the previous edition: choose a past event of the same sport and circuit that is not deleted.",
  PROPOSAL_NOT_EDITABLE: proposalMessage("PROPOSAL_NOT_EDITABLE"),
}
// A past event of the platform, searched by name or number (`GET /admin/events`); the change read checks that it can be taken.
const eventOption = (found, timeZone) => {
  const zone = found.timezone || timeZone
  const days = { start: moment(found.startDate).tz(zone).format("YYYY-MM-DD"), end: moment(found.endDate).tz(zone).format("YYYY-MM-DD") }
  return { label: `#${found.eventId} ${found.name} · ${daysYearLabel(days)}`, value: String(found._id || found.id) }
}

// The change dialog (ticket 09): an event, or "No previous edition"; then "Take the technical characteristics of #new" (each old → new, with
// who edited the old value) or "Keep the current values"; a Proposed or Confirmed proposal then asks "Tell the organizer?". One edit at the
// proposal's revision; the name and the competitions never change through it.
export function PreviousEditionDialog({ proposal, context, operation, onClose, onSaved, onReload }) {
  const [chosen, setChosen] = useState(null)
  const [noEdition, setNoEdition] = useState(false)
  const [telling, setTelling] = useState(null)
  const [message, setMessage] = useState(null)
  const [failure, setFailure] = useState("")
  const read = useChangeRead(proposal.id, proposal.revision, chosen?.value || null)
  const currentEdition = read.data?.current
  const next = chosen ? read.data?.next : null
  const load = async input => {
    setFailure("")
    if (input.trim().length < 2) return []
    try {
      const response = await get("/admin/events?" + new URLSearchParams({ search: input.trim(), timeframe: "past", sportType: proposal.sportType, page: 1, limit: 20 }))
      if (!Array.isArray(response?.data?.results)) throw new Error("Invalid response")
      return response.data.results.map(found => eventOption(found, context.timeZone))
    } catch (error) {
      setFailure("Could not load events. Type again to retry.")
      return []
    }
  }
  const send = async ({ previousEdition, take, tellOrganizer }) => {
    setMessage(null)
    const result = await operation.run("edit", "/admin/tournament-proposals/" + proposal.id, { revision: proposal.revision, changes: { previousEdition },
      ...(take && { take: true }), ...(tellOrganizer !== undefined && { tellOrganizer }) }, "patch")
    if (!result || ["cancelled", "busy"].includes(result.status)) return
    if (result.status === "saved") { onSaved(); return }
    setTelling(null)
    setMessage(result.status === "unknown" ? result.message || UNCONFIRMED : result.message)
    if (result.status === "unknown" || RELOADING_CODES.includes(result.code)) onReload()
  }
  // Take or Keep, then "Tell the organizer?" when the organizer has the proposal already.
  const choose = command => (ASKS_TO_TELL.includes(proposal.state) ? setTelling(command) : send(command))
  const target = noEdition ? null : chosen && { eventId: chosen.value, source: "chosen" }
  const capacityOf = competitionId => (proposal.competitions || []).find(({ id }) => id === competitionId)?.mlMaxNumber
  const busy = Boolean(operation.pending)
  const actions = telling ? null : noEdition ? <button type="button" className="btn btn-primary" disabled={busy}
    onClick={() => choose({ previousEdition: null })}>Remove the previous edition</button>
    : next && <>
      <button type="button" className="btn btn-light" disabled={busy} onClick={() => choose({ previousEdition: target })}>Keep the current values</button>
      <button type="button" className="btn btn-primary" disabled={busy} onClick={() => choose({ previousEdition: target, take: true })}>
        {`Take the technical characteristics of #${next.eventNumber}`}</button>
    </>
  return <PlannerDialog title="Change the previous edition" sub={`${proposal.number} · the name and the competitions stay as they are`} wide onClose={onClose}
    actions={actions}>
    <div className="ep-stack ep-gap">
      {!telling && <>
        <div className="ep-field">
          <label htmlFor="previous-edition-event">Event</label>
          <AsyncSelect inputId="previous-edition-event" classNamePrefix="ep-select" {...portalMenu} loadOptions={load} value={chosen} isDisabled={busy}
            placeholder="Search a past event by name or #number" noOptionsMessage={() => "Type at least two letters, or the event's number"}
            onChange={option => { setNoEdition(false); setChosen(option) }} />
          {failure && <small className="text-danger">{failure}</small>}
        </div>
        {proposal.previousEdition && <button type="button" className={"btn btn-sm align-self-start " + (noEdition ? "btn-dark" : "btn-light")}
          aria-pressed={noEdition} disabled={busy} onClick={() => { setChosen(null); setNoEdition(true) }}>No previous edition</button>}
        {chosen && read.error && <span className="text-danger">{READ_REFUSALS[read.error] || "The event could not be checked. Choose it again."}</span>}
        {(next || noEdition) && <b>{`${shortLabel(currentEdition)} → ${shortLabel(next)}`}</b>}
        {next?.cancelled && <span className="ep-mark ep-mark-error">Cancelled</span>}
        {next?.alsoPreviousEditionOf?.length > 0 && <span className="ep-mark ep-mark-warn">
          {`Also the previous edition of ${next.alsoPreviousEditionOf.map(({ number }) => number).join(", ")}`}</span>}
        {next && (read.data.take.length ? <ul className="ep-take-list" aria-label="What Take changes">
          {read.data.take.map(change => <li key={change.field} className="ep-stack">
            <span>{changeText(change, context)}{" "}<SourcePill source={sourcePill(change.source)} /></span>
            {change.editedBy && <span className="ep-meta">{`Edited by ${change.editedBy.name || "Unknown author"}, ${instantLabel(change.editedBy.at, context.timeZone)}`}</span>}
            {/* Take keeps a competition's capacity and replaces its groups (D3; Tudor's Q2 A): say so on that row. */}
            {/^competitions\.[^.]+\.numberOfGroups$/.test(change.field) && <span className="ep-meta">
              {`The Main List stays ${capacityOf(change.field.split(".")[1])} places: check the capacity after taking.`}</span>}
          </li>)}
        </ul> : <span className="ep-meta">Taking it changes no value: the technical characteristics are the same.</span>)}
      </>}
      {telling && <div className="ep-stack ep-gap" role="group" aria-label="Tell the organizer">
        <b>Tell the organizer?</b>
        <span className="ep-meta">Yes puts the organizer in To send with what changed; sending stays yours. No new confirmation is asked either way.</span>
        <span className="ep-generate-actions">
          <button type="button" className="btn btn-primary" disabled={busy} onClick={() => send({ ...telling, tellOrganizer: true })}>Yes, tell the organizer</button>
          <button type="button" className="btn btn-light" disabled={busy} onClick={() => send({ ...telling, tellOrganizer: false })}>No, don't tell the organizer</button>
        </span>
      </div>}
      {message && <span className="text-danger">{message}</span>}
    </div>
  </PlannerDialog>
}
